import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styles from './StepNavigation.module.css';

// Friendly labels for step names (matches AppliedProducts / ReviewSelectionsModal).
const STEP_TITLE_MAP = {
    'Floor': 'Floor',
    'Counters': 'Countertop',
    'Backsplash': 'Backsplash',
    'BathFloor': 'Bathroom Flooring',
    'ShowerFloor': 'Shower Floor',
    'ShowerWall': 'Shower Wall',
    'Cabinets': 'Cabinets',
    'KitchenFaucets': 'Kitchen Faucets',
    'Mirrors': 'Mirrors',
    'BathPlumbing': 'Bathroom Plumbing',
    'Paint': 'Paint',
    'KitchenArrangement': 'Kitchen Arrangement',
    'ShowerType': 'Shower Type',
    'BathType': 'Bath Type'
};

const getStepLabel = (step) => {
    if (!step) return '';
    // Prefer a params.title if provided (Generic steps set this), else use step name mapping.
    if (step.params && step.params.title) return step.params.title;
    const name = step.name;
    if (name && STEP_TITLE_MAP[name]) return STEP_TITLE_MAP[name];
    return name ? name.replace(/([A-Z])/g, ' $1').trim() : '';
};

export default function StepNavigation({
    onNext,
    onPrevious,
    canGoNext,
    canGoPrevious
}) {
    const activeSteps = useSelector(state => state.visualizer.activeSteps);
    const currentStepIndex = useSelector(state => state.visualizer.currentStepIndex);

    const currentStep = activeSteps[currentStepIndex] ?? null;
    const prevStep = currentStepIndex > 0 ? activeSteps[currentStepIndex - 1] : null;
    const nextStep = currentStepIndex < activeSteps.length - 1 ? activeSteps[currentStepIndex + 1] : null;

    const currentLabel = getStepLabel(currentStep);
    const prevLabel = getStepLabel(prevStep);
    const nextLabel = getStepLabel(nextStep);

    const previousEnabled = canGoPrevious ?? !!prevStep;
    const nextEnabled = canGoNext ?? !!nextStep;

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                {/* Previous side */}
                <button
                    type="button"
                    className={styles.side}
                    onClick={onPrevious}
                    disabled={!previousEnabled}
                    aria-label={prevLabel ? `Previous step: ${prevLabel}` : 'Previous step'}
                >
                    <span className={styles.navButton} aria-hidden="true">
                        <svg viewBox="0 0 6 10" fill="none" className={styles.navIcon}>
                            <path
                                d="M5.896907216494845 4.769539078156313 C5.965635738831616 4.836339345357381 6 4.91315965263861 6 5 C6 5.086840347361389 5.965635738831616 5.163660654642618 5.896907216494845 5.230460921843687 L1.092783505154639 9.899799599198397 C1.0240549828178693 9.966599866399466 0.9450171821305842 10 0.8556701030927835 10 C0.766323024054983 10 0.6872852233676976 9.966599866399466 0.6185567010309279 9.899799599198397 L0.10309278350515465 9.39879759519038 C0.0343642611683849 9.331997327989312 0 9.255177020708082 0 9.168336673346694 C0 9.081496325985302 0.0343642611683849 9.004676018704075 0.10309278350515465 8.937875751503006 L4.154639175257731 5 L0.10309278350515465 1.062124248496994 C0.0343642611683849 0.9953239812959247 0 0.9185036740146957 0 0.8316633266533069 C0 0.7448229792919159 0.0343642611683849 0.6680026720106869 0.10309278350515465 0.6012024048096187 L0.6185567010309279 0.1002004008016033 C0.6872852233676976 0.033400133600534065 0.766323024054983 0 0.8556701030927835 0 C0.9450171821305842 0 1.0240549828178693 0.033400133600534065 1.092783505154639 0.1002004008016033 L5.896907216494845 4.769539078156313 Z"
                                fill="white"
                                transform="scale(-1, 1) translate(-6, 0)"
                            />
                        </svg>
                    </span>
                    <span className={styles.textBlock}>
                        <span className={styles.label}>PREVIOUS</span>
                        <span className={styles.stepName}>{prevLabel}</span>
                    </span>
                </button>

                {/* Next side */}
                <button
                    type="button"
                    className={`${styles.side} ${styles.right}`}
                    onClick={onNext}
                    disabled={!nextEnabled}
                    aria-label={nextLabel ? `Next step: ${nextLabel}` : 'Next step'}
                >
                    <span className={`${styles.textBlock} ${styles.textRight}`}>
                        <span className={styles.label}>NEXT</span>
                        <span className={styles.stepName}>{nextLabel}</span>
                    </span>
                    <span className={styles.navButton} aria-hidden="true">
                        <svg viewBox="0 0 6 10" fill="none" className={styles.navIcon}>
                            <path
                                d="M5.896907216494845 4.769539078156313 C5.965635738831616 4.836339345357381 6 4.91315965263861 6 5 C6 5.086840347361389 5.965635738831616 5.163660654642618 5.896907216494845 5.230460921843687 L1.092783505154639 9.899799599198397 C1.0240549828178693 9.966599866399466 0.9450171821305842 10 0.8556701030927835 10 C0.766323024054983 10 0.6872852233676976 9.966599866399466 0.6185567010309279 9.899799599198397 L0.10309278350515465 9.39879759519038 C0.0343642611683849 9.331997327989312 0 9.255177020708082 0 9.168336673346694 C0 9.081496325985302 0.0343642611683849 9.004676018704075 0.10309278350515465 8.937875751503006 L4.154639175257731 5 L0.10309278350515465 1.062124248496994 C0.0343642611683849 0.9953239812959247 0 0.9185036740146957 0 0.8316633266533069 C0 0.7448229792919159 0.0343642611683849 0.6680026720106869 0.10309278350515465 0.6012024048096187 L0.6185567010309279 0.1002004008016033 C0.6872852233676976 0.033400133600534065 0.766323024054983 0 0.8556701030927835 0 C0.9450171821305842 0 1.0240549828178693 0.033400133600534065 1.092783505154639 0.1002004008016033 L5.896907216494845 4.769539078156313 Z"
                                fill="white"
                            />
                        </svg>
                    </span>
                </button>
            </div>
            {currentLabel && (
                <div className={styles.currentStepTitle}>{currentLabel}</div>
            )}
        </div>
    );
}

StepNavigation.propTypes = {
    onNext: PropTypes.func,
    onPrevious: PropTypes.func,
    canGoNext: PropTypes.bool,
    canGoPrevious: PropTypes.bool
};
